import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Field, inputClass, selectClass } from './BackgroundFields';
import ColorFillField from './ColorFillField';
import WeekScheduleEditor from './WeekScheduleEditor';
import {
  computeClickCollectStats,
  formatPriceTtc,
  getCommerceCapabilities,
  siteHasClickCollectComponent,
  siteHasReservationComponent,
} from '../utils/commerce';
import ClickCollectMenuCatalogEditor from './ClickCollectMenuCatalogEditor';
import ClickCollectPaymentAdmin from './ClickCollectPaymentAdmin';
import {
  DEFAULT_CLICK_COLLECT_PAYMENT,
  normalizeClickCollectPayment,
  serializeClickCollectPaymentForSave,
} from '../utils/commercePricing';
import {
  DEFAULT_CLICK_COLLECT_DISPLAY,
  DEFAULT_MENU_THEME,
  DEFAULT_RESERVATION_DISPLAY,
  normalizeMenuTheme,
} from '../utils/commerceDisplay';
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_STYLES,
  RES_STATUS_LABELS,
  confirmedCoversOnDay,
  dayKeyFromIso,
  WEEKDAY_KEYS,
  defaultPickupWeek,
  defaultServiceWeek,
  normalizeDaySlot,
  formatOrderDate,
  formatReservationSlot,
  normalizeOrderStatus,
} from '../utils/commerceAdmin';
import { buildCommerceAdminTabs } from '../utils/commerce';
import CommerceHubupAlertsSettings from './CommerceHubupAlertsSettings';
import {
  normalizeCommerceHubup,
  serializeCommerceHubupForSave,
} from '../utils/commerceHubup';

const MENU_ADMIN_SECTIONS = [
  { id: 'products', label: 'Produits', icon: 'UtensilsCrossed' },
  { id: 'text', label: 'Textes carte', icon: 'Type' },
  { id: 'appearance', label: 'Apparence', icon: 'Palette' },
  { id: 'hours', label: 'Horaires', icon: 'Clock' },
  { id: 'payment', label: 'Paiement', icon: 'CreditCard' },
];

export default function SiteCommerceAdminPanel({
  site,
  published,
  saving,
  onSave,
  tab,
  onTabChange,
  showUnpublishedOverlay = true,
}) {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reservation, setReservation] = useState({
    introHtml: '',
    maxPartySize: 6,
    leadTimeHours: 2,
  });
  const [reservationRequests, setReservationRequests] = useState([]);
  const [ccDisplay, setCcDisplay] = useState({ ...DEFAULT_CLICK_COLLECT_DISPLAY });
  const [resDisplay, setResDisplay] = useState({ ...DEFAULT_RESERVATION_DISPLAY });
  const [pickupWeek, setPickupWeek] = useState(defaultPickupWeek());
  const [serviceWeek, setServiceWeek] = useState(defaultServiceWeek());
  const [ticketError, setTicketError] = useState('');
  const [menuSection, setMenuSection] = useState('products');
  const [ccPayment, setCcPayment] = useState({ ...DEFAULT_CLICK_COLLECT_PAYMENT });
  const [commerceHubup, setCommerceHubup] = useState(() => normalizeCommerceHubup(null));

  const tabs = useMemo(() => buildCommerceAdminTabs(site), [site]);

  useEffect(() => {
    if (!site) return;
    setItems(Array.isArray(site.clickAndCollect?.items) ? [...site.clickAndCollect.items] : []);
    setOrders(Array.isArray(site.clickAndCollectOrders) ? [...site.clickAndCollectOrders] : []);
    setReservation({
      introHtml: String(site.reservation?.introHtml || ''),
      maxPartySize: Number(site.reservation?.maxPartySize) || 6,
      leadTimeHours: Number(site.reservation?.leadTimeHours) || 2,
      coversPerDay: Number(site.reservation?.coversPerDay) || 40,
    });
    const pickupDef = defaultPickupWeek();
    const pickupRaw =
      site.clickAndCollect?.pickupWeek && typeof site.clickAndCollect.pickupWeek === 'object'
        ? site.clickAndCollect.pickupWeek
        : {};
    setPickupWeek(
      WEEKDAY_KEYS.reduce((acc, k) => {
        acc[k] = normalizeDaySlot(pickupRaw[k], pickupDef[k]);
        return acc;
      }, {}),
    );
    const serviceDef = defaultServiceWeek();
    const serviceRaw =
      site.reservation?.serviceWeek && typeof site.reservation.serviceWeek === 'object'
        ? site.reservation.serviceWeek
        : {};
    setServiceWeek(
      WEEKDAY_KEYS.reduce((acc, k) => {
        acc[k] = normalizeDaySlot(serviceRaw[k], serviceDef[k]);
        return acc;
      }, {}),
    );
    setReservationRequests(
      Array.isArray(site.reservationRequests) ? [...site.reservationRequests] : [],
    );
    const disp =
      site.clickAndCollect?.display && typeof site.clickAndCollect.display === 'object'
        ? site.clickAndCollect.display
        : {};
    setCcDisplay({
      ...DEFAULT_CLICK_COLLECT_DISPLAY,
      ...disp,
      menuTheme: normalizeMenuTheme(disp.menuTheme || DEFAULT_CLICK_COLLECT_DISPLAY.menuTheme),
    });
    setCcPayment(
      normalizeClickCollectPayment(site.clickAndCollect?.payment || DEFAULT_CLICK_COLLECT_PAYMENT),
    );
    setResDisplay({
      ...DEFAULT_RESERVATION_DISPLAY,
      ...(site.reservation?.display && typeof site.reservation.display === 'object'
        ? site.reservation.display
        : {}),
    });
    setCommerceHubup(normalizeCommerceHubup(site.commerceHubup));
  }, [site?._id, site?.updatedAt]);

  useEffect(() => {
    if (!tabs.some((t) => t.id === tab) && tabs[0]) {
      onTabChange?.(tabs[0].id);
    }
  }, [tab, tabs, onTabChange]);

  const stats = useMemo(() => computeClickCollectStats(orders), [orders]);
  const caps = getCommerceCapabilities(site);

  if (!site) return null;

  const buildSavePatch = (overrides = {}) => ({
    clickAndCollect: {
      ...(site.clickAndCollect || {}),
      items,
      display: ccDisplay,
      pickupWeek,
      payment: serializeClickCollectPaymentForSave(ccPayment),
      ...overrides.clickAndCollect,
    },
    clickAndCollectOrders: overrides.clickAndCollectOrders ?? orders,
    reservation: {
      ...reservation,
      display: resDisplay,
      serviceWeek,
      ...overrides.reservation,
    },
    reservationRequests: overrides.reservationRequests ?? reservationRequests,
    commerceHubup: serializeCommerceHubupForSave(
      overrides.commerceHubup ?? commerceHubup,
    ),
  });

  const handleSave = () => {
    onSave?.(buildSavePatch());
  };

  const savePatch = (overrides) => {
    onSave?.(buildSavePatch(overrides));
  };

  const patchOrderStatus = (orderId, status) => {
    const nextOrders = orders.map((o) =>
      o.id === orderId ? { ...o, status: normalizeOrderStatus(status) } : o,
    );
    setOrders(nextOrders);
    savePatch({ clickAndCollectOrders: nextOrders });
  };

  const patchReservationStatus = (requestId, status) => {
    const req = reservationRequests.find((r) => r.id === requestId);
    if (status === 'confirmed' && req) {
      const dayKey = dayKeyFromIso(req.preferredDate);
      const booked = confirmedCoversOnDay(reservationRequests, dayKey);
      const cap = Number(reservation.coversPerDay) || 40;
      if (booked + (Number(req.partySize) || 0) > cap) {
        setTicketError('Capacité du jour dépassée — impossible de confirmer cette réservation.');
        return;
      }
    }
    setTicketError('');
    const next = reservationRequests.map((r) =>
      r.id === requestId ? { ...r, status } : r,
    );
    setReservationRequests(next);
    savePatch({ reservationRequests: next });
  };

  const patchItem = (id, patch) => {
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    );
  };

  const addImages = (id, files) => {
    const fileList = Array.from(files || []);
    if (!fileList.length) return;
    fileList.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const url = String(reader.result || '');
        if (!url) return;
        setItems((prev) =>
          prev.map((it) => {
            if (it.id !== id) return it;
            const next = [...(it.imageUrls || []), url].slice(0, 6);
            return { ...it, imageUrls: next };
          }),
        );
      };
      reader.readAsDataURL(file);
    });
  };

  return (
    <div className="flex flex-col min-h-0 w-full">
      <div className="flex-1 min-w-0 w-full flex flex-col min-h-[420px]">
        <div className="relative flex-1 overflow-hidden flex flex-col bg-white dark:bg-neutral-950">
          {!published && showUnpublishedOverlay ? (
            <div className="absolute inset-0 z-20 bg-background/80 backdrop-blur-[2px] flex items-center justify-center p-8 text-center">
              <div className="max-w-md space-y-3">
                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="Lock" size={22} className="text-muted-foreground" />
                </div>
                <p className="text-base font-semibold text-foreground">Site non publié</p>
                <p className="text-sm text-muted-foreground">
                  Les visiteurs ne peuvent pas encore commander ni réserver. Vous pouvez préparer
                  votre menu et vos réglages ici, puis publier depuis l&apos;éditeur.
                </p>
              </div>
            </div>
          ) : null}

          <div className="flex-1 overflow-y-auto p-5 sm:p-6 lg:p-8 space-y-6">
            {ticketError ? (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                {ticketError}
              </p>
            ) : null}
            {tab === 'overview' ? (
              <div className="w-full space-y-6">
                <h2 className="text-lg font-semibold text-foreground text-center">Vue d&apos;ensemble</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {caps.clickAndCollect ? (
                    <>
                      <StatCard label="Terminées" value={stats.salesCount} />
                      <StatCard label="Chiffre d'affaires" value={formatPriceTtc(stats.revenueTtc)} />
                      <StatCard label="À valider" value={stats.pendingCount} />
                      <StatCard label="En cours" value={stats.inProgressCount || 0} />
                    </>
                  ) : null}
                  {caps.reservation ? (
                    <StatCard
                      label="Demandes table"
                      value={reservationRequests.filter((r) => r.status !== 'cancelled').length}
                      className={caps.clickAndCollect ? '' : 'col-span-2 lg:col-span-4'}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}

            {tab === 'menu' && caps.clickAndCollect ? (
              <div className="w-full flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch min-h-0">
                <nav
                  className="w-full lg:w-56 xl:w-60 shrink-0 lg:sticky lg:top-0 self-start p-1 flex lg:flex-col gap-0.5 overflow-x-auto"
                  aria-label="Sous-menu menu"
                >
                  {MENU_ADMIN_SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setMenuSection(section.id)}
                      className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap transition-colors text-left w-full ${
                        menuSection === section.id
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                      }`}
                    >
                      <Icon name={section.icon} size={16} className="shrink-0" />
                      {section.label}
                    </button>
                  ))}
                </nav>

                <div className="flex-1 min-w-0 w-full space-y-4">
                  {menuSection === 'products' ? (
                    <>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Produits</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Choisissez une catégorie dans la liste, puis remplissez les fiches produit.
                        </p>
                      </div>
                      <ClickCollectMenuCatalogEditor
                        items={items}
                        orders={orders}
                        layout={ccDisplay.layout || 'classic'}
                        onLayoutChange={(layout) => setCcDisplay((d) => ({ ...d, layout }))}
                        onItemsChange={setItems}
                        onPatchItem={patchItem}
                        onAddImages={addImages}
                      />
                    </>
                  ) : null}

                  {menuSection === 'text' ? (
                    <>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Textes carte</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Titre, sous-titre et informations en bas de la carte client.
                        </p>
                      </div>
                      <div className="space-y-4 pt-2">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <Field
                            label="Nom sur la carte"
                            hint="Ex. nom du restaurant — laissez vide pour utiliser le titre du site"
                          >
                            <input
                              className={inputClass}
                              value={ccDisplay.pageTitle || ''}
                              onChange={(e) =>
                                setCcDisplay((d) => ({ ...d, pageTitle: e.target.value }))
                              }
                              placeholder={site.title || 'La Bonne Table'}
                            />
                          </Field>
                          <Field label="Sous le nom" hint="Ex. « vous propose »">
                            <input
                              className={inputClass}
                              value={ccDisplay.pageSubtitle || ''}
                              onChange={(e) =>
                                setCcDisplay((d) => ({ ...d, pageSubtitle: e.target.value }))
                              }
                              placeholder="vous propose"
                            />
                          </Field>
                        </div>
                        <Field
                          label="Pied de carte"
                          hint="Une ligne par info (horaires, moyens de paiement, adresse…)"
                        >
                          <textarea
                            className={`${inputClass} min-h-[100px] font-mono text-xs`}
                            value={ccDisplay.menuFooter || ''}
                            onChange={(e) =>
                              setCcDisplay((d) => ({ ...d, menuFooter: e.target.value }))
                            }
                            placeholder={
                              'Ouvert du mardi au samedi\nPaiement CB et espèces\n12 rue Example, Lyon'
                            }
                          />
                        </Field>
                      </div>
                    </>
                  ) : null}

                  {menuSection === 'appearance' ? (
                    <>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Apparence</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Présentation, couleurs, ornements et boutons du panier.
                        </p>
                      </div>
                      <div className="space-y-4 pt-2">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <Field label="Présentation client">
                            <select
                              className={selectClass}
                              value={ccDisplay.layout || 'classic'}
                              onChange={(e) =>
                                setCcDisplay((d) => ({ ...d, layout: e.target.value }))
                              }
                            >
                              <option value="classic">Carte restaurant</option>
                              <option value="gallery">Grille avec photos</option>
                            </select>
                          </Field>
                          <Field label="Couleur du texte (carte)">
                            <ColorFillField
                              value={ccDisplay.menuTheme?.inkColor}
                              onChange={(fill) =>
                                setCcDisplay((d) => ({
                                  ...d,
                                  menuTheme: { ...normalizeMenuTheme(d.menuTheme), inkColor: fill },
                                }))
                              }
                              fallback={DEFAULT_MENU_THEME.inkColor}
                            />
                          </Field>
                          <Field label="Fond autour de la carte">
                            <ColorFillField
                              value={ccDisplay.menuTheme?.pageBackground}
                              onChange={(fill) =>
                                setCcDisplay((d) => ({
                                  ...d,
                                  menuTheme: {
                                    ...normalizeMenuTheme(d.menuTheme),
                                    pageBackground: fill,
                                  },
                                }))
                              }
                              fallback={DEFAULT_MENU_THEME.pageBackground}
                            />
                          </Field>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4">
                          <label className="flex items-start gap-3 text-sm cursor-pointer rounded-lg bg-muted/25 p-3 flex-1">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={ccDisplay.menuTheme?.showOrnaments !== false}
                              onChange={(e) =>
                                setCcDisplay((d) => ({
                                  ...d,
                                  menuTheme: {
                                    ...normalizeMenuTheme(d.menuTheme),
                                    showOrnaments: e.target.checked,
                                  },
                                }))
                              }
                            />
                            <span>
                              <span className="font-medium block">Ornements dans les coins</span>
                              <span className="text-xs text-muted-foreground">
                                Déco florale de la carte imprimée
                              </span>
                            </span>
                          </label>
                          <label className="flex items-start gap-3 text-sm cursor-pointer rounded-lg bg-muted/25 p-3 flex-1">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={ccDisplay.menuTheme?.showFrame !== false}
                              onChange={(e) =>
                                setCcDisplay((d) => ({
                                  ...d,
                                  menuTheme: {
                                    ...normalizeMenuTheme(d.menuTheme),
                                    showFrame: e.target.checked,
                                  },
                                }))
                              }
                            />
                            <span>
                              <span className="font-medium block">Cadre double</span>
                              <span className="text-xs text-muted-foreground">
                                Bordures haut / bas / côtés
                              </span>
                            </span>
                          </label>
                        </div>
                        <div className="pt-6 grid sm:grid-cols-2 gap-4">
                          <Field label="Bouton « Valider la commande »">
                            <ColorFillField
                              value={ccDisplay.orderButtonColor}
                              onChange={(fill) =>
                                setCcDisplay((d) => ({ ...d, orderButtonColor: fill }))
                              }
                              fallback="#432728"
                            />
                          </Field>
                          <Field label="Texte du bouton">
                            <ColorFillField
                              value={ccDisplay.orderButtonTextColor}
                              onChange={(fill) =>
                                setCcDisplay((d) => ({ ...d, orderButtonTextColor: fill }))
                              }
                              fallback="#ffffff"
                            />
                          </Field>
                        </div>
                      </div>
                    </>
                  ) : null}

                  {menuSection === 'hours' ? (
                    <>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Horaires de commande</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Jours ouverts et créneaux (plusieurs plages par jour possibles).
                        </p>
                      </div>
                      <WeekScheduleEditor
                        title="Jours et créneaux"
                        subtitle="Ex. midi et soir, avec coupures."
                        value={pickupWeek}
                        onChange={setPickupWeek}
                      />
                    </>
                  ) : null}

                  {menuSection === 'payment' ? (
                    <>
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Paiement en ligne</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Clés Stancer propres à ce site (fonctionnalité temporaire).
                        </p>
                      </div>
                      <ClickCollectPaymentAdmin payment={ccPayment} onChange={setCcPayment} />
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}

            {tab === 'orders' && caps.clickAndCollect ? (
              <div className="w-full space-y-4">
                <h2 className="text-lg font-semibold text-foreground">Commandes</h2>
                {orders.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-8 text-center">
                    Aucune commande pour le moment.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {[...orders]
                      .sort(
                        (a, b) =>
                          new Date(b.createdAt || 0).getTime() -
                          new Date(a.createdAt || 0).getTime(),
                      )
                      .map((order) => (
                        <OrderTicket
                          key={order.id}
                          order={order}
                          onStatus={patchOrderStatus}
                          disabled={saving}
                        />
                      ))}
                  </ul>
                )}
              </div>
            ) : null}

            {tab === 'reservation' && caps.reservation ? (
              <div className="w-full space-y-8">
                <h2 className="text-lg font-semibold text-foreground">Prise de table</h2>
                <WeekScheduleEditor
                  title="Jours ouverts"
                  subtitle="Créneaux multiples par jour ; les jours fermés sont grisés côté client."
                  value={serviceWeek}
                  onChange={setServiceWeek}
                />
                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <Field label="Titre du formulaire">
                    <input
                      className={inputClass}
                      value={resDisplay.formTitle || ''}
                      onChange={(e) =>
                        setResDisplay((d) => ({ ...d, formTitle: e.target.value }))
                      }
                    />
                  </Field>
                  <Field label="Sous-titre">
                    <input
                      className={inputClass}
                      value={resDisplay.formSubtitle || ''}
                      onChange={(e) =>
                        setResDisplay((d) => ({ ...d, formSubtitle: e.target.value }))
                      }
                    />
                  </Field>
                  <Field label="Couleur d'accent (calendrier)">
                    <ColorFillField
                      value={resDisplay.accentColor}
                      onChange={(fill) => setResDisplay((d) => ({ ...d, accentColor: fill }))}
                      fallback="#0f766e"
                    />
                  </Field>
                  <Field label="Fond de page">
                    <ColorFillField
                      value={resDisplay.backgroundColor}
                      onChange={(fill) =>
                        setResDisplay((d) => ({ ...d, backgroundColor: fill }))
                      }
                      fallback="#f8fafc"
                    />
                  </Field>
                  <Field label="Bouton d'envoi">
                    <ColorFillField
                      value={resDisplay.buttonColor}
                      onChange={(fill) => setResDisplay((d) => ({ ...d, buttonColor: fill }))}
                      fallback="#0f766e"
                    />
                  </Field>
                  <Field label="Texte du bouton">
                    <ColorFillField
                      value={resDisplay.buttonTextColor}
                      onChange={(fill) =>
                        setResDisplay((d) => ({ ...d, buttonTextColor: fill }))
                      }
                      fallback="#ffffff"
                    />
                  </Field>
                </div>

                <div className="space-y-4 pt-8">
                <Field label="Message d'accueil">
                  <textarea
                    className={`${inputClass} min-h-[120px]`}
                    value={reservation.introHtml}
                    onChange={(e) =>
                      setReservation((r) => ({ ...r, introHtml: e.target.value }))
                    }
                    placeholder="Précisez vos créneaux, conditions…"
                  />
                </Field>
                <Field
                  label="Places maximum par jour"
                  hint="Comptabilisées après confirmation d'une demande."
                >
                  <input
                    type="number"
                    min="1"
                    max="500"
                    className={inputClass}
                    value={reservation.coversPerDay ?? 40}
                    onChange={(e) =>
                      setReservation((r) => ({
                        ...r,
                        coversPerDay: Math.max(1, Number(e.target.value) || 40),
                      }))
                    }
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Convives maximum">
                    <input
                      type="number"
                      min="1"
                      max="50"
                      className={inputClass}
                      value={reservation.maxPartySize}
                      onChange={(e) =>
                        setReservation((r) => ({
                          ...r,
                          maxPartySize: Math.max(1, Number(e.target.value) || 1),
                        }))
                      }
                    />
                  </Field>
                  <Field label="Préavis (heures)">
                    <input
                      type="number"
                      min="0"
                      className={inputClass}
                      value={reservation.leadTimeHours}
                      onChange={(e) =>
                        setReservation((r) => ({
                          ...r,
                          leadTimeHours: Math.max(0, Number(e.target.value) || 0),
                        }))
                      }
                    />
                  </Field>
                </div>
                </div>
              </div>
            ) : null}

            {tab === 'hubup-alerts' ? (
              <div className="w-full">
                <div className="mb-6 text-center w-full">
                  <h2 className="text-lg font-semibold text-foreground">Alertes HubUP</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Notifiez votre équipe sur le fil mobile à chaque nouvelle activité.
                  </p>
                </div>
                <CommerceHubupAlertsSettings
                  value={commerceHubup}
                  onChange={setCommerceHubup}
                  reservationCap={caps.reservation}
                  clickCollectCap={caps.clickAndCollect}
                />
              </div>
            ) : null}

            {tab === 'res-requests' && caps.reservation ? (
              <div className="w-full space-y-4">
                <h2 className="text-lg font-semibold text-foreground text-center">Demandes clients</h2>
                {reservationRequests.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-8 text-center">
                    Aucune demande.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {[...reservationRequests]
                      .sort(
                        (a, b) =>
                          new Date(b.createdAt || 0).getTime() -
                          new Date(a.createdAt || 0).getTime(),
                      )
                      .map((req) => (
                        <ReservationTicket
                          key={req.id}
                          request={req}
                          coversPerDay={reservation.coversPerDay || 40}
                          allRequests={reservationRequests}
                          onStatus={patchReservationStatus}
                          disabled={saving}
                        />
                      ))}
                  </ul>
                )}
              </div>
            ) : null}
          </div>

          <div className="px-5 py-4 flex justify-end gap-2 shrink-0">
            <Button type="button" variant="success" disabled={saving} onClick={handleSave}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, className = '' }) {
  return (
    <div className={`py-2 ${className}`}>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums mt-1.5 text-foreground">{value}</p>
    </div>
  );
}

function OrderTicket({ order, onStatus, disabled }) {
  const status = normalizeOrderStatus(order.status);
  const badge = ORDER_STATUS_STYLES[status] || ORDER_STATUS_STYLES.pending;
  return (
    <li className="py-5 space-y-3 border-b border-neutral-100 last:border-0 dark:border-neutral-800">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-foreground">{order.customerName}</p>
          <p className="text-xs text-muted-foreground">
            {formatOrderDate(order.createdAt)} · {order.customerEmail}
            {order.customerPhone ? ` · ${order.customerPhone}` : ''}
          </p>
          {order.pickupAt ? (
            <p className="text-xs text-muted-foreground mt-0.5">
              Retrait : {formatReservationSlot(order.pickupAt)}
            </p>
          ) : null}
          {order.paymentStatus === 'awaiting' ? (
            <p className="text-xs text-amber-700 mt-0.5">Paiement Stancer en attente</p>
          ) : null}
          {order.paymentStatus === 'paid' ? (
            <p className="text-xs text-emerald-700 mt-0.5">Payé en ligne (Stancer)</p>
          ) : null}
        </div>
        <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-full ${badge}`}>
          {ORDER_STATUS_LABELS[status] || status}
        </span>
      </div>
      <ul className="text-sm space-y-1">
        {(order.lines || []).map((line, idx) => (
          <li key={`${order.id}-l-${idx}`} className="flex justify-between gap-2">
            <span>
              {line.quantity}× {line.title}
            </span>
            <span className="tabular-nums">{formatPriceTtc(line.unitPriceTtc * line.quantity)}</span>
          </li>
        ))}
      </ul>
      <p className="text-sm font-semibold">Total {formatPriceTtc(order.totalTtc)} TTC</p>
      {order.pickupNote ? (
        <p className="text-xs text-muted-foreground pl-0">{order.pickupNote}</p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        {status === 'pending' ? (
          <>
            <Button
              type="button"
              size="sm"
              disabled={disabled}
              onClick={() => onStatus(order.id, 'confirmed')}
            >
              Confirmer
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => onStatus(order.id, 'cancelled')}
            >
              Refuser
            </Button>
          </>
        ) : null}
        {status === 'confirmed' ? (
          <>
            <Button type="button" size="sm" disabled={disabled} onClick={() => onStatus(order.id, 'ready')}>
              Marquer prête
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => onStatus(order.id, 'cancelled')}
            >
              Annuler
            </Button>
          </>
        ) : null}
        {status === 'ready' ? (
          <Button
            type="button"
            size="sm"
            variant="success"
            disabled={disabled}
            onClick={() => onStatus(order.id, 'completed')}
          >
            Terminée
          </Button>
        ) : null}
      </div>
    </li>
  );
}

function ReservationTicket({ request, coversPerDay, allRequests, onStatus, disabled }) {
  const status = request.status || 'pending';
  const dayKey = dayKeyFromIso(request.preferredDate);
  const booked = confirmedCoversOnDay(allRequests, dayKey);
  const resLabel = RES_STATUS_LABELS[status] || status;
  return (
    <li className="py-5 space-y-2 border-b border-neutral-100 last:border-0 dark:border-neutral-800">
      <div className="flex flex-wrap justify-between gap-2">
        <div>
          <p className="font-semibold text-foreground">{request.customerName}</p>
          <p className="text-xs text-muted-foreground">
            {formatReservationSlot(request.preferredDate)} · {request.partySize} pers.
          </p>
          <p className="text-xs text-muted-foreground">
            {request.customerEmail}
            {request.customerPhone ? ` · ${request.customerPhone}` : ''}
          </p>
        </div>
        <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-full bg-muted">
          {resLabel}
        </span>
      </div>
      {request.note ? (
        <p className="text-xs text-muted-foreground">{request.note}</p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        {status === 'pending' ? (
          <>
            <Button
              type="button"
              size="sm"
              disabled={disabled}
              onClick={() => onStatus(request.id, 'confirmed')}
            >
              Confirmer
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => onStatus(request.id, 'cancelled')}
            >
              Refuser
            </Button>
          </>
        ) : null}
        {status === 'confirmed' ? (
          <>
            <Button
              type="button"
              size="sm"
              variant="success"
              disabled={disabled}
              onClick={() => onStatus(request.id, 'completed')}
            >
              Terminée
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => onStatus(request.id, 'cancelled')}
            >
              Annuler
            </Button>
          </>
        ) : null}
      </div>
    </li>
  );
}
